'use client';

import * as stylex from '@stylexjs/stylex';
import { useMotionValueEvent, useScroll } from 'motion/react';
import { useRef, useState } from 'react';
import { typography } from '../../styles/typography';
import { PageSection } from '../PageSection';
import { Header } from '../Header';
import { PageSectionsLandingHero } from '@/tina/__generated__/types';
import { RichText } from '../RichText';
import { tinaField } from 'tinacms/react';
import '../../styles/tokens.stylex';
import { borderRadius, easing, size, spacing, timing } from '../../styles/tokens.stylex';
import { HighlightList } from '../HighlightList';
import { NextLinkWithEventPayload } from '../NextLinkWithEventPayload';
import { ReviewsIoLogo } from '../svg/images/reviewsIo';
import { FiveStars } from '../svg/images/fiveStars';
import { a11y } from '../../styles/a11y';
import Image from 'next/image';
import { ConversionForm } from '../ConversionForm';
import type { ConversionFormPreviewMode } from '../ConversionForm/ConversionForm';
import { MaxColumnGrid } from '../MaxColumnGrid';
interface LandingHeroProps {
  data: PageSectionsLandingHero;
}
export const LandingHero = ({
  data
}: LandingHeroProps) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const [previewMode, setPreviewMode] = useState<ConversionFormPreviewMode>('default');
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    const next = latest >= 0.5 ? 'minimised' : 'default';
    setPreviewMode(prev => prev === next ? prev : next);
  });
  return <div ref={heroRef} className="xrvj5dj xogrr9c x1n2onr6 x18y3g3h">
      <Header color="dark" elevation={false} styles={[sx.header, sx.staggerIn(0)]} />
      <PageSection bgVariant="dark" styles={sx.pageSection} renderInner={<MaxColumnGrid columnBehaviour="auto-fit" maxColumns={2} />}>
        <div className="x16pmaob x1y8v6su xrvj5dj x7wzq59 x86je6w">
          <h1 {...stylex.props(typography.display1, sx.staggerIn(1))} data-tina-field={tinaField(data, 'heading')}>
            {data.heading}
          </h1>
          <RichText data-tina-field={tinaField(data, 'intro')} wrapperStyles={sx.staggerIn(2)}>
            {data.intro}
          </RichText>
          <HighlightList data-tina-field={tinaField(data, 'highlights')} data={data.highlights} styles={sx.staggerIn(3)} />
          {data.reviewStatement && <div {...stylex.props(sx.reviewsIoWrapper, sx.staggerIn(4))}>
              <FiveStars size="small" />
              <p {...stylex.props(typography.body, sx.reviewsStatement)} data-tina-field={tinaField(data, 'reviewStatement')}>
                {data.reviewStatement}
              </p>
              <NextLinkWithEventPayload href="https://www.reviews.io/company-reviews/store/https-www.aomail.com.au" target="_blank" rel="noopener noreferrer" clickEventPayload={{
            event: 'reviews_IO_click',
            location: 'hero'
          }}>
                <ReviewsIoLogo size="small" />
                <span {...stylex.props(a11y.visuallyHidden)}>
                  Read our Reviews on Reviews.io
                </span>
              </NextLinkWithEventPayload>
            </div>}
          <div {...stylex.props(sx.heroImageWrapper, sx.staggerIn(5))}>
            {data.heroImage && <Image className="xl1xv1r" alt={data.heroImageAlt as string} src={data.heroImage as string} fill sizes="420px, (max-width: 768px) 100vw (max-width: 1200px) 50vw, 33vw" priority />}
          </div>
        </div>
        <div className="x-default-marker x1y8v6su xfeetku x12h1iku x7wzq59 x1355qak x86je6w">
          {data.conversionForm && <ConversionForm data={data.conversionForm} previewMode={previewMode} />}
        </div>
      </PageSection>
    </div>;
};
const _temp = {
  kSiTet: "x4itv7f " + "x1hc1fzr",
  k3aq6I: "xz8iu5t " + "x1c1b4dv",
  kIr0Dl: "xlohyk8",
  kIyJzY: "xhrev5j",
  k1ekBW: "x6o7n8i",
  kAMwcw: "xq2r7pn",
  k6sLGO: "x6my1t9",
  "$$css": true
};
const sx = {
  header: {
    kGO01o: "x1ywm7j1",
    kLKAdn: "x1navgqx",
    $$css: true
  },
  pageSection: {
    kLKAdn: "xexx8yu",
    $$css: true
  },
  reviewsIoWrapper: {
    kOIVth: "x3ybb33",
    k1xSpc: "xrvj5dj",
    $$css: true
  },
  reviewsStatement: {
    kybGjl: "x1hl2dhg",
    $$css: true
  },
  heroImageWrapper: {
    kaIpWk: "x1o5z2k8",
    kVQacm: "xb3r6kr",
    keoZOQ: "xyi9k72",
    kVAEAm: "x1n2onr6",
    kAzted: "x712feu",
    $$css: true
  },
  staggerIn: (delay: number) => [_temp, {
    "--x-transitionDelay": (val => typeof val === "number" ? val + "ms" : val != null ? val : undefined)(`${delay * 0.1}s`)
  }]
};